import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { selectIsUserLogged } from '@selectors/auth';

import { getRouteUrl } from '@utils/urls';
import { getSession } from '@utils/jwt';
import { clearSession } from '@utils/auth';

import Card from '@common/components/Card';
import Layout from '@common/containers/Layout';
import RouteLayout from '@common/containers/RouteLayout';
import { VIP_CARD_CLIPPED } from '@common/images';
import DeepLinks from '@common/components/DeepLinks';
import {
  HOME,
  LOGIN,
  PRE_LOGIN,
  PRE_REGISTER,
  VIP_CARD_LOGIN,
} from '@common/routes';

import {
  CardsWrapper,
  FormIcon,
  LoginIcon,
  VIPCard,
  VIPCardCard,
  VIPCardContainer,
  VIPCardHr,
  VIPCardTitle,
} from './PreLogin.styled';

export function PreLogin() {
  const { t } = useTranslation('common');
  const { query, locale, isReady } = useRouter();
  const dispatch = useDispatch();
  const isUserLogged = useSelector(selectIsUserLogged);
  const [target, setTarget] = useState(query?.target);
  const pageUrl = getRouteUrl(locale, PRE_LOGIN);

  useEffect(() => {
    if (isReady) setTarget(query?.target);
  }, [isReady, query]);

  const getToken = useCallback(async () => {
    const session = await getSession();

    return session?.accessToken;
  }, []);

  const logoutUser = useCallback(
    async () => clearSession(dispatch),
    [dispatch],
  );

  useEffect(() => {
    const token = getToken();

    if (isUserLogged && !token) {
      logoutUser();
    }
  }, [isUserLogged]);

  return (
    <>
      <DeepLinks customAsPath={target || undefined} />

      <Layout
        title={t('preLogin.metaTitle', 'Pre-Login')}
        canonicalPageUrl={pageUrl}
      >
        <RouteLayout
          title={t('preLogin.title', 'Login')}
          stickyTitle={t('preLogin.title', 'Login')}
          columns={8}
          backRoute={HOME}
          backRouteAs={getRouteUrl(locale, HOME)}
        >
          <CardsWrapper>
            <Card
              href={{
                pathname: LOGIN,
                query: { target },
              }}
              icon={<LoginIcon />}
              title={t('preLogin.vipLogin', 'VIP-Login')}
              titleAs="h2"
              as={{
                pathname: getRouteUrl(locale, LOGIN),
                query: { target },
              }}
            />
            <Card
              href={PRE_REGISTER}
              icon={<FormIcon />}
              title={t('preLogin.register', 'Registrieren')}
              titleAs="h2"
              as={getRouteUrl(locale, PRE_REGISTER)}
            />
          </CardsWrapper>
          <Link
            legacyBehavior
            href={VIP_CARD_LOGIN}
            passHref
            as={getRouteUrl(locale, VIP_CARD_LOGIN)}
          >
            <VIPCardCard>
              <VIPCardContainer>
                <VIPCard src={VIP_CARD_CLIPPED} />
              </VIPCardContainer>
              <VIPCardHr />
              <VIPCardTitle>
                {t(
                  'preLogin.youOnlyHaveVIPCard',
                  'Du hast nur eine VIP-Karte?',
                )}
              </VIPCardTitle>
            </VIPCardCard>
          </Link>
        </RouteLayout>
      </Layout>
    </>
  );
}

export default PreLogin;
